import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, Building2, Plus, ChevronRight, Loader2, Check,
  Instagram, Facebook, Twitter, Youtube, Linkedin, MoreVertical, Trash2, Edit
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const PLATFORM_ICONS = {
  youtube: Youtube,
  linkedin: Linkedin,
  twitter: Twitter,
  instagram: Instagram,
  facebook: Facebook,
  tiktok: TikTokIcon
};

/**
 * AgencyPanel Component
 * Agency mode for managing multiple clients and their social accounts
 */
export default function AgencyPanel() {
  const [loading, setLoading] = useState(true);
  const [agencyStatus, setAgencyStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({
    name: '',
    company_name: '',
    email: '',
    industry: '',
    notes: ''
  });
  const [addingClient, setAddingClient] = useState(false);

  useEffect(() => {
    fetchAgencyStatus();
  }, []);

  const fetchAgencyStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agency/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencyStatus(response.data);
      
      if (response.data.is_agency) {
        fetchClients();
        fetchDashboard();
      }
    } catch (error) {
      console.error('Failed to fetch agency status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agency/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agency/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const enableAgency = async () => {
    setEnabling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/agency/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Agency mode enabled!');
      fetchAgencyStatus();
    } catch (error) {
      toast.error('Failed to enable agency mode');
    } finally {
      setEnabling(false);
    }
  };

  const addClient = async () => {
    if (!newClient.name || !newClient.company_name || !newClient.email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setAddingClient(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/agency/clients`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client added!');
      setShowAddClient(false);
      setNewClient({ name: '', company_name: '', email: '', industry: '', notes: '' });
      fetchClients();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to add client');
    } finally {
      setAddingClient(false);
    }
  };

  const deleteClient = async (clientId) => {
    if (!confirm('Are you sure you want to archive this client?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/agency/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client archived');
      fetchClients();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to archive client');
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/5 animate-pulse">
        <CardContent className="p-6">
          <div className="h-32 bg-white/5 rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-white/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Agency Mode
            {agencyStatus?.is_agency && (
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                Active
              </Badge>
            )}
          </CardTitle>
          {agencyStatus?.is_agency && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAddClient(true)}
              data-testid="add-client-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Client
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!agencyStatus?.is_agency ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Manage multiple client accounts and their social media presence from one dashboard.
            </p>
            <div className="grid grid-cols-1 gap-2 text-xs">
              {agencyStatus?.features?.length > 0 ? (
                agencyStatus.features.slice(0, 4).map((feature, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-muted-foreground">
                    <Check className="w-3 h-3 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))
              ) : (
                <>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Check className="w-3 h-3 text-green-500" />
                    <span>Manage multiple client accounts</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Check className="w-3 h-3 text-green-500" />
                    <span>Create social accounts on behalf of clients</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Check className="w-3 h-3 text-green-500" />
                    <span>Secure encrypted credential storage</span>
                  </div>
                </>
              )}
            </div>
            <Button 
              onClick={enableAgency} 
              disabled={enabling}
              className="w-full"
              data-testid="enable-agency-btn"
            >
              {enabling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Building2 className="w-4 h-4 mr-2" />
                  Enable Agency Mode
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Agency Stats */}
            {dashboard && (
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 rounded bg-white/5 text-center">
                  <p className="text-lg font-bold">{dashboard.total_clients}</p>
                  <p className="text-xs text-muted-foreground">Clients</p>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <p className="text-lg font-bold">{dashboard.active_clients}</p>
                  <p className="text-xs text-muted-foreground">Active</p>
                </div>
                <div className="p-2 rounded bg-white/5 text-center">
                  <p className="text-lg font-bold">{dashboard.total_social_accounts}</p>
                  <p className="text-xs text-muted-foreground">Accounts</p>
                </div>
              </div>
            )}

            {/* Client List */}
            {clients.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {clients.map((client) => (
                  <motion.div
                    key={client.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="p-3 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{client.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{client.company_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {client.industry || 'General'}
                        </Badge>
                        {client.social_accounts?.length > 0 && (
                          <div className="flex -space-x-1">
                            {client.social_accounts.slice(0, 3).map((acc, i) => {
                              const Icon = PLATFORM_ICONS[acc.platform] || Users;
                              return (
                                <div key={i} className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center border border-white/20">
                                  <Icon className="w-3 h-3" />
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <MoreVertical className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setSelectedClient(client)}>
                              <Edit className="w-3 h-3 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-400"
                              onClick={() => deleteClient(client.id)}
                            >
                              <Trash2 className="w-3 h-3 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-muted-foreground">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No clients yet. Add your first client!
              </div>
            )}
          </div>
        )}

        {/* Add Client Modal */}
        <AnimatePresence>
          {showAddClient && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowAddClient(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="glass-card rounded-xl p-6 w-full max-w-md border border-white/10"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold mb-4">Add New Client</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Contact Name *</label>
                    <input
                      type="text"
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none"
                      value={newClient.name}
                      onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                      placeholder="John Smith"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Company Name *</label>
                    <input
                      type="text"
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none"
                      value={newClient.company_name}
                      onChange={(e) => setNewClient({...newClient, company_name: e.target.value})}
                      placeholder="Acme Corp"
                      data-testid="client-company-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Email *</label>
                    <input
                      type="email"
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none"
                      value={newClient.email}
                      onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                      placeholder="john@acme.com"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Industry</label>
                    <input
                      type="text"
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none"
                      value={newClient.industry}
                      onChange={(e) => setNewClient({...newClient, industry: e.target.value})}
                      placeholder="Technology"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Notes</label>
                    <textarea
                      className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:border-primary focus:outline-none resize-none"
                      rows={2}
                      value={newClient.notes}
                      onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowAddClient(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1"
                    onClick={addClient}
                    disabled={addingClient}
                    data-testid="save-client-btn"
                  >
                    {addingClient ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Add Client'
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
